import { useEffect, useRef } from 'react';

const bullets = [
  { icon: 'ri-bar-chart-line', text: 'Pollution trend analysis across 24+ years' },
  { icon: 'ri-git-branch-line', text: 'Comparison with other major rivers in India' },
  { icon: 'ri-search-eye-line', text: 'Identification of key pollution factors' },
  { icon: 'ri-robot-line', text: 'Machine learning predictions till 2030' },
];

const stats = [
  { value: '15+', label: 'Data Sources' },
  { value: '24+', label: 'Years of Data' },
  { value: '50+', label: 'Pollution Hotspots' },
];

export default function AboutProject() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.animate-in').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).classList.add('opacity-100', 'translate-y-0');
                (el as HTMLElement).classList.remove('opacity-0', 'translate-y-6');
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="py-24 md:py-32 bg-[#F8F5F0]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Image */}
          <div className="animate-in opacity-0 translate-y-6 transition-all duration-700">
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/cc683fb4892d9c0021f0175918aab882.jpg"
                alt="Ganga River"
                className="w-full h-[420px] object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1B4332]/40 to-transparent" />
              {/* Floating stat cards */}
              <div className="absolute bottom-6 left-6 right-6 flex gap-3">
                {stats.map((s) => (
                  <div key={s.label} className="flex-1 bg-[#F8F5F0]/90 backdrop-blur-sm rounded-xl p-3 text-center">
                    <div className="text-xl font-bold text-[#1B4332]" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</div>
                    <div className="text-xs text-[#6C757D] mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Content */}
          <div className="flex flex-col gap-6">
            <div className="animate-in opacity-0 translate-y-6 transition-all duration-700">
              <p className="text-[#2A9D8F] text-sm uppercase tracking-[0.25em] font-semibold mb-3">About the Project</p>
              <h2
                className="text-3xl md:text-4xl font-bold text-[#1C1C1C] leading-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Echoes of the Ganga
              </h2>
            </div>

            <div className="animate-in opacity-0 translate-y-6 transition-all duration-700">
              <p className="text-[#6C757D] text-base leading-relaxed">
                Echoes of the Ganga is a data-driven environmental initiative focused on analyzing pollution trends and identifying key contamination patterns in the Ganga River using long-term datasets.
              </p>
            </div>

            <div className="animate-in opacity-0 translate-y-6 transition-all duration-700">
              <p className="text-[#6C757D] text-base leading-relaxed">
                Data has been collected from official government sources including the <strong className="text-[#1B4332]">Namami Gange Programme</strong>, <strong className="text-[#1B4332]">Central Pollution Control Board (CPCB)</strong>, and the <strong className="text-[#1B4332]">Ministry of Jal Shakti</strong>, along with supporting research and environmental reports.
              </p>
            </div>

            <div className="animate-in opacity-0 translate-y-6 transition-all duration-700">
              <p className="text-[#6C757D] text-base leading-relaxed">
                The dataset spans from <strong className="text-[#1C1C1C]">2000 to 2024</strong>, covering the entire river from <strong className="text-[#1C1C1C]">Gangotri (origin)</strong> to <strong className="text-[#1C1C1C]">West Bengal (end point)</strong>.
              </p>
            </div>

            <div className="animate-in opacity-0 translate-y-6 transition-all duration-700">
              <div className="bg-[#EDE0D4] rounded-2xl p-6 border border-[#6B4F3A]/20">
                <p className="text-[#6B4F3A] font-semibold text-sm mb-4 uppercase tracking-wide">Project Includes</p>
                <div className="flex flex-col gap-3">
                  {bullets.map((b) => (
                    <div key={b.text} className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F8F5F0] text-[#2A9D8F] flex-shrink-0">
                        <i className={`${b.icon} text-sm`} />
                      </div>
                      <span className="text-[#1C1C1C] text-sm">{b.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
